import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import { withTypes, FormProps } from "react-final-form";

import Alert from "@mui/material/Alert";
import { makeStyles } from "helpers/StyleHelper";
import { useTheme } from "@mui/material/styles";
import {
  Grid,
  Button,
  DialogContent,
  DialogActions,
  TextField,
  Theme,
} from "@mui/material";

import Field from "components/Field";
import { IRatingSetting, RatingSetting } from "services/KnowfullyAdminClient";

const useStyles = makeStyles((theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
  subtitle: {
    margin: theme.spacing(3, 0, 2),
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  checkboxContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
}));

const { Form } = withTypes<IRatingSetting>();

type QuestionRatingFormProps = {
  rating?: RatingSetting;
  submitLabel: string;
  onSubmit: (values: IRatingSetting) => void;
  onCancel?: () => void;
};

const QuestionForm = ({
  rating,
  submitLabel,
  onCancel,
  onSubmit,
}: QuestionRatingFormProps & FormProps<IRatingSetting>) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());

  const init = useMemo(() => rating ?? {}, [rating]);

  return useMemo(
    () => (
      <Form
        onSubmit={onSubmit}
        initialValues={init}
        render={({ handleSubmit, submitting, submitError }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Field name="name" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField variant="standard"
                        fullWidth
                        label={t({ id: "questionRatingNameLabel" })}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        data-cy="ratingNameText"
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="minValueText" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField variant="standard"
                        fullWidth
                        label={t({ id: "questionRatingMinLabel" })}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        data-cy="minText"
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="maxValueText" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField variant="standard"
                        fullWidth
                        label={t({ id: "questionRatingMaxLabel" })}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        data-cy="maxText"
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="optionCount" type="number" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField variant="standard"
                        fullWidth
                        label={t({ id: "questionRatingCountLabel" })}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        data-cy="countText"
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                {submitError && (
                  <Alert sx={styles.feedback} severity="error">
                    {submitError}
                  </Alert>
                )}
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button disabled={submitting} onClick={onCancel} data-cy="cancelButton">
                {t({ id: "cancelButton" })}
              </Button>

              <Button type="submit" color="primary" disabled={submitting} data-cy="submitButton">
                {submitLabel}
              </Button>
            </DialogActions>
          </form>
        )}
      />
    ),
    [styles.feedback, init, onCancel, onSubmit, submitLabel, t]
  );
};

export default QuestionForm;
